import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { UserForm } from "./UserForm";
import { UserPermissionsModal } from "./UserPermissionsModal";
import { UserActivityModal } from "./UserActivityModal";
import { formatDateTime } from "../../utils/formatDate";
import type { Role, UserListItem } from "../../types";

export default function UserList() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<UserListItem | null | undefined>(undefined);
  const [permTarget, setPermTarget] = useState<UserListItem | null>(null);
  const [activityTarget, setActivityTarget] = useState<UserListItem | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get<UserListItem[]>("/api/users"), api.get<Role[]>("/api/roles")])
      .then(([u, r]) => { setUsers(u); setRoles(r); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggleActive(u: UserListItem) {
    try {
      await api.patch(`/api/users/${u.id}/status`, { isActive: !u.is_active });
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    }
  }

  async function resetPassword(u: UserListItem) {
    const newPassword = prompt(`Neues Passwort für ${u.full_name} (mind. 8 Zeichen):`);
    if (!newPassword) return;
    try {
      await api.put(`/api/users/${u.id}/password`, { newPassword });
      alert("Passwort wurde zurückgesetzt.");
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    }
  }

  async function handleDelete(u: UserListItem) {
    if (!confirm(`Benutzer "${u.full_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/users/${u.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-100">Benutzerverwaltung</h1>
        <button className="btn-primary" onClick={() => setEditing(null)}>+ Neuer Benutzer</button>
      </div>

      <Card padded={false} className="overflow-x-auto">
        {loading ? <Spinner /> : users.length === 0 ? <EmptyState message="Keine Benutzer vorhanden." /> : (
          <table className="table-base">
            <thead>
              <tr><th>Name</th><th>Benutzername</th><th>Rolle</th><th>Status</th><th>Letzter Login</th><th /></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="font-medium text-gray-100">{u.full_name}</td>
                  <td className="text-gray-400">{u.username}</td>
                  <td><Badge tone="accent">{u.role_name}</Badge></td>
                  <td>{u.is_active ? <Badge tone="success">Aktiv</Badge> : <Badge tone="gray">Deaktiviert</Badge>}</td>
                  <td className="text-gray-500 text-xs">{u.last_login_at ? formatDateTime(u.last_login_at) : "—"}</td>
                  <td className="text-right whitespace-nowrap space-x-2">
                    <button className="text-accent text-xs hover:underline" onClick={() => setEditing(u)}>Bearbeiten</button>
                    <button className="text-accent text-xs hover:underline" onClick={() => setPermTarget(u)}>Rechte</button>
                    <button className="text-accent text-xs hover:underline" onClick={() => setActivityTarget(u)}>Aktivität</button>
                    <button className="text-gray-400 text-xs hover:underline" onClick={() => resetPassword(u)}>Passwort</button>
                    {u.id !== currentUser?.id && (
                      <>
                        <button className="text-warning text-xs hover:underline" onClick={() => toggleActive(u)}>{u.is_active ? "Deaktivieren" : "Aktivieren"}</button>
                        <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(u)}>Löschen</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {editing !== undefined && (
        <UserForm user={editing} roles={roles} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
      {permTarget && <UserPermissionsModal user={permTarget} onClose={() => setPermTarget(null)} />}
      {activityTarget && <UserActivityModal user={activityTarget} onClose={() => setActivityTarget(null)} />}
    </div>
  );
}
